import { Device } from '@capacitor/device';
import { ref } from 'vue';
import { MediaSession } from '@darkpo13/capacitor-media-session';

export function useMediaControls() {
  const musicControlsCreated = ref(false);

  const buildArtwork = (artwork) => [
    {
      src: artwork,
      sizes: '512x512',
      type: 'image/jpeg',
    },
  ];

  async function setMediaSession({
    title,
    artist,
    artwork,
    canSkipBack,
    canSkipNext,
    hasScrubbing,
    onControl,
  }) {
    const { platform } = await Device.getInfo();
    const metadata = {
      title,
      artist,
      artwork: buildArtwork(artwork),
    };

    if (platform === 'web' || platform === 'ios') {
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata(metadata);

        navigator.mediaSession.setActionHandler('play', () => {
          navigator.mediaSession.playbackState = 'playing';
          onControl('music-controls-play');
        });

        navigator.mediaSession.setActionHandler('pause', () => {
          navigator.mediaSession.playbackState = 'paused';
          onControl('music-controls-pause');
        });

        if (canSkipBack) {
          navigator.mediaSession.setActionHandler('previoustrack', () =>
            onControl('music-controls-previous'),
          );
        }

        if (canSkipNext) {
          navigator.mediaSession.setActionHandler('nexttrack', () =>
            onControl('music-controls-next'),
          );
        }

        if (hasScrubbing) {
          // TODO: Implement seekto functionality
          // navigator.mediaSession.setActionHandler('seekto', (details) => {
          //   onControl('music-controls-seekto', details);
          // });
        }
      }
    } else {
      MediaSession.setMetadata(metadata);

      MediaSession.setActionHandler({ action: 'play' }, () => {
        onControl('music-controls-play');
      });

      MediaSession.setActionHandler({ action: 'pause' }, () => {
        onControl('music-controls-pause');
      });

      if (canSkipBack) {
        MediaSession.setActionHandler({ action: 'previoustrack' }, () => {
          onControl('music-controls-previous');
        });
      }

      if (canSkipNext) {
        MediaSession.setActionHandler({ action: 'nexttrack' }, () => {
          onControl('music-controls-next');
        });
      }
    }
  }

  async function createMusicControls({
    title,
    artist,
    cover,
    canSkipBack,
    canSkipNext,
    onControl,
    hasScrubbing,
  }) {
    try {
      await setMediaSession({
        title,
        artist,
        artwork: cover,
        canSkipBack,
        canSkipNext,
        hasScrubbing,
        onControl,
      });
      musicControlsCreated.value = true;
    } catch (err) {
      console.error('Error creating music controls:', err);
      musicControlsCreated.value = false;
    }
  }

  async function syncMusicControls(isPlaying, init = false, createFn) {
    if (init && typeof createFn === 'function') {
      await createFn();
    }

    const state = isPlaying ? 'playing' : 'paused';
    const { platform } = await Device.getInfo();

    if (platform === 'web' || platform === 'ios') {
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        navigator.mediaSession.playbackState = state;
      }
    } else {
      MediaSession.setPlaybackState({ playbackState: state });
    }
  }

  function destroyMusicControls() {
    musicControlsCreated.value = false;
  }

  return {
    createMusicControls,
    syncMusicControls,
    destroyMusicControls,
    setMediaSession,
  };
}
